import { config } from '@/config';

export class ApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly details?: JsonValue;
  readonly errorId?: string;
  readonly correlationId?: string;

  constructor(
    message: string,
    opts?: {
      status?: number;
      code?: string;
      details?: JsonValue;
      errorId?: string;
      correlationId?: string;
    },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = opts?.status;
    this.code = opts?.code;
    this.details = opts?.details;
    this.errorId = opts?.errorId;
    this.correlationId = opts?.correlationId;
  }
}

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string | null;
  preferProvidedToken?: boolean;
  headers?: Record<string, string>;
  orgId?: string | null;
  retries?: number;
  ignoreOrgRemoval?: boolean;
  skipAuthRecovery?: boolean;
};

type RawRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: BodyInit | null;
  token?: string | null;
  preferProvidedToken?: boolean;
  headers?: Record<string, string>;
  orgId?: string | null;
  retries?: number;
  ignoreOrgRemoval?: boolean;
  skipAuthRecovery?: boolean;
};

type ErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    details?: JsonValue;
    correlationId?: string;
    errorId?: string;
  };
  code?: string;
  message?: string;
  details?: JsonValue;
  correlationId?: string;
  errorId?: string;
};

let authErrorHandler: ((status: number) => Promise<boolean>) | undefined;
let authTokenProvider: (() => string | null) | undefined;
let authRefreshHandler: (() => Promise<boolean>) | undefined;
let orgIdProvider: (() => string | null) | undefined;
let authRecoveryInFlight: Promise<boolean> | null = null;
let authRecoveryCooldownUntilMs = 0;
const ORG_REMOVED_EVENT = 'flowqa-org-removed';
export const SESSION_EXPIRED_EVENT = 'flowqa-session-expired';
export const COOKIE_AUTH_TOKEN_PREFIX = 'cookie:';
export const buildCookieAuthToken = (userId: string): string =>
  `${COOKIE_AUTH_TOKEN_PREFIX}${userId}`;
export const isCookieAuthToken = (token: string): boolean =>
  token.startsWith(COOKIE_AUTH_TOKEN_PREFIX);
const CSRF_COOKIE = 'flowqa_csrf';
const E2E_ACCESS_TOKEN_KEY = 'flowqa-e2e-access-token';
let shouldRefreshAuthAfterVisibility = false;
let visibilityRefreshInFlight: Promise<void> | null = null;

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      shouldRefreshAuthAfterVisibility = true;
    }
  });
}

const hasE2eAccessToken = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.localStorage.getItem(E2E_ACCESS_TOKEN_KEY));
  } catch {
    return false;
  }
};

export const setAuthErrorHandler = (handler?: (status: number) => Promise<boolean>): void => {
  authErrorHandler = handler;
  authRecoveryInFlight = null;
  authRecoveryCooldownUntilMs = 0;
};

export const setAuthTokenProvider = (provider?: () => string | null): void => {
  authTokenProvider = provider;
};

export const setAuthRefreshHandler = (handler?: () => Promise<boolean>): void => {
  authRefreshHandler = handler;
};

export const getAuthToken = (): string | null => (authTokenProvider ? authTokenProvider() : null);

export const setOrgIdProvider = (provider?: () => string | null): void => {
  orgIdProvider = provider;
};

const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split(';').map((part) => part.trim());
  for (const part of parts) {
    if (!part.startsWith(`${name}=`)) continue;
    return decodeURIComponent(part.slice(name.length + 1));
  }
  return null;
};

const getCsrfToken = (): string | null => getCookieValue(CSRF_COOKIE);

const toApiError = async (response: Response): Promise<ApiError> => {
  let payload: ErrorPayload | undefined;
  try {
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      payload = (await response.json()) as ErrorPayload;
    }
  } catch {
    // ignore parse errors
  }
  const errorIdHeader = response.headers.get('x-error-id') ?? undefined;
  const resolved = payload?.error ?? payload;
  const message = resolved?.message ?? `Request failed (${response.status})`;
  return new ApiError(message, {
    status: response.status,
    code: resolved?.code,
    details: resolved?.details,
    correlationId: resolved?.correlationId,
    errorId: resolved?.errorId ?? errorIdHeader,
  });
};

const shouldClearOrgContext = (error: ApiError, status: number): boolean => {
  if (status !== 403) return false;
  if (error.code !== 'FORBIDDEN') return false;
  return error.message.toLowerCase().includes('not a member of this organisation');
};

const shouldExpireSession = (error: ApiError, status: number): boolean => {
  const message = error.message.toLowerCase();
  if (error.code === 'AUTH_FAILED') {
    return message.includes('user not found');
  }
  if (status === 404 && error.code === 'NOT_FOUND') {
    return message.includes('user not found') || message.includes('organisation not found');
  }
  return false;
};

type ResponseErrorResult = { apiErr: ApiError; recovered: boolean; attemptedAuthRecover: boolean };

const runAuthRecovery = async (status: number): Promise<boolean> => {
  if (!authErrorHandler) return false;
  const now = Date.now();
  if (now < authRecoveryCooldownUntilMs) {
    return false;
  }
  if (authRecoveryInFlight) {
    return await authRecoveryInFlight;
  }
  authRecoveryInFlight = (async (): Promise<boolean> => {
    try {
      const recovered = await authErrorHandler(status);
      if (!recovered) {
        authRecoveryCooldownUntilMs = Date.now() + 5_000;
      }
      return recovered;
    } finally {
      authRecoveryInFlight = null;
    }
  })();
  return await authRecoveryInFlight;
};

const handleResponseError = async (
  response: Response,
  {
    hasAuthToken,
    ignoreOrgRemoval,
    attemptedAuthRecover,
    skipAuthRecovery,
  }: {
    hasAuthToken: boolean;
    ignoreOrgRemoval: boolean;
    attemptedAuthRecover: boolean;
    skipAuthRecovery: boolean;
  },
): Promise<ResponseErrorResult> => {
  const apiErr = await toApiError(response);
  let nextAttemptedAuthRecover = attemptedAuthRecover;
  if (!skipAuthRecovery) {
    if (
      !attemptedAuthRecover &&
      (response.status === 401 || response.status === 403) &&
      authErrorHandler
    ) {
      nextAttemptedAuthRecover = true;
      const recovered = await runAuthRecovery(response.status);
      if (recovered) {
        return { apiErr, recovered: true, attemptedAuthRecover: nextAttemptedAuthRecover };
      }
    }
    if (
      !ignoreOrgRemoval &&
      shouldClearOrgContext(apiErr, response.status) &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new Event(ORG_REMOVED_EVENT));
      if (!hasE2eAccessToken()) {
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
    }
    if (
      hasAuthToken &&
      shouldExpireSession(apiErr, response.status) &&
      typeof window !== 'undefined' &&
      !hasE2eAccessToken()
    ) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    }
  }
  return { apiErr, recovered: false, attemptedAuthRecover: nextAttemptedAuthRecover };
};

const buildUrl = (path: string): string => {
  if (path.startsWith('http')) return path;
  return `${config.apiBaseUrl}/${path.replace(/^\/+/, '')}`;
};

const normaliseJsonValue = (value: unknown): JsonValue => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value)) as JsonValue;
  } catch {
    return null;
  }
};

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const decodeBase64Url = (value: string): string | null => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (normalized.length % 4)) % 4;
  const padded = `${normalized}${'='.repeat(padLength)}`;
  if (typeof globalThis.atob === 'function') {
    return globalThis.atob(padded);
  }
  return null;
};

const parseJwtExpiryMs = (token: string): number | null => {
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  const payloadSegment = segments[1];
  if (!payloadSegment) return null;
  try {
    const decoded = decodeBase64Url(payloadSegment);
    if (!decoded) return null;
    const parsed: unknown = JSON.parse(decoded);
    if (!parsed || typeof parsed !== 'object') return null;
    const exp = (parsed as { exp?: unknown }).exp;
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
    return exp * 1000;
  } catch {
    return null;
  }
};

const maybeRefreshAuthAfterVisibility = async ({
  path,
  skipAuthRecovery,
}: {
  path: string;
  skipAuthRecovery: boolean;
}): Promise<void> => {
  if (skipAuthRecovery) return;
  if (!shouldRefreshAuthAfterVisibility) return;
  if (!authRefreshHandler) return;
  if (path.endsWith('/auth/bootstrap') || path.endsWith('/api/auth/bootstrap')) return;
  if (visibilityRefreshInFlight) {
    await visibilityRefreshInFlight;
    return;
  }
  visibilityRefreshInFlight = (async (): Promise<void> => {
    try {
      await authRefreshHandler();
    } catch {
      // Ignore proactive refresh errors; request-level handling still applies.
    } finally {
      shouldRefreshAuthAfterVisibility = false;
      visibilityRefreshInFlight = null;
    }
  })();
  await visibilityRefreshInFlight;
};

type ResponseParser<T> = (response: Response) => Promise<T>;

const parseJsonOrUndefined = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return (await response.json()) as T;
  }
  return undefined as T;
};

const parseJsonOrText = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return (await response.json()) as T;
  }
  return (await response.text()) as T;
};

const executeRequest = async <T>({
  path,
  method,
  headers,
  body,
  token: providedToken,
  preferProvidedToken,
  orgId: providedOrgId,
  retries,
  ignoreOrgRemoval,
  skipAuthRecovery,
  parseResponse,
}: {
  path: string;
  method: RequestOptions['method'];
  headers: Record<string, string>;
  body?: BodyInit;
  token?: string | null;
  preferProvidedToken: boolean;
  orgId?: string | null;
  retries: number;
  ignoreOrgRemoval: boolean;
  skipAuthRecovery: boolean;
  parseResponse: ResponseParser<T>;
}): Promise<T> => {
  const url = buildUrl(path);
  let attempt = 0;
  let lastError: Error | null = null;
  let attemptedAuthRecover = false;
  let preferFreshAuthToken = false;
  const proactiveRefreshWindowMs = 15_000;

  while (attempt <= retries) {
    await maybeRefreshAuthAfterVisibility({ path, skipAuthRecovery });
    const liveToken = getAuthToken();
    const token = preferFreshAuthToken
      ? (liveToken ?? providedToken ?? null)
      : preferProvidedToken
        ? (providedToken ?? liveToken ?? null)
        : (liveToken ?? providedToken ?? null);
    if (!skipAuthRecovery && authRefreshHandler && token && !isCookieAuthToken(token)) {
      const expiryMs = parseJwtExpiryMs(token);
      if (expiryMs !== null && expiryMs - Date.now() <= proactiveRefreshWindowMs) {
        try {
          const refreshed = await authRefreshHandler();
          if (refreshed) {
            preferFreshAuthToken = true;
            continue;
          }
        } catch {
          // Ignore proactive refresh failures; request-level handling still applies.
        }
      }
    }
    const hasAuthToken = Boolean(token);
    const orgId =
      providedOrgId === undefined ? (orgIdProvider ? orgIdProvider() : undefined) : providedOrgId;
    const baseHeaders: Record<string, string> = { ...headers };
    if (token && !isCookieAuthToken(token)) {
      baseHeaders.Authorization = `Bearer ${token}`;
    }
    if (method !== 'GET' && !baseHeaders['x-csrf-token']) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        baseHeaders['x-csrf-token'] = csrfToken;
      }
    }
    if (orgId === null) {
      baseHeaders['x-org-id'] = '';
    } else if (typeof orgId === 'string' && orgId.length > 0) {
      baseHeaders['x-org-id'] = orgId;
    }
    try {
      const response = await fetch(url, {
        method,
        headers: baseHeaders,
        body,
        credentials: 'include',
      });

      if (!response.ok) {
        const canRetryWithLiveToken =
          !preferFreshAuthToken &&
          (response.status === 401 || response.status === 403) &&
          typeof token === 'string' &&
          typeof liveToken === 'string' &&
          token !== liveToken;
        if (canRetryWithLiveToken) {
          // When callers capture a stale token in closures, retry once with the latest
          // provider token before invoking broader auth recovery.
          preferFreshAuthToken = true;
          continue;
        }
        const result = await handleResponseError(response, {
          hasAuthToken,
          ignoreOrgRemoval,
          attemptedAuthRecover,
          skipAuthRecovery,
        });
        attemptedAuthRecover = result.attemptedAuthRecover;
        if (result.recovered) {
          // If auth recovered, retry with the latest token from provider instead of
          // reusing a potentially stale token captured by the caller.
          preferFreshAuthToken = true;
          continue;
        }
        throw result.apiErr;
      }

      return await parseResponse(response);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      lastError = error;
      const isApiError = error instanceof ApiError;
      const status = isApiError ? (error.status ?? 0) : 0;
      const shouldRetry = attempt < retries && (status >= 500 || status === 0);
      if (!shouldRetry) {
        throw error;
      }
      attempt += 1;
      await sleep(200 * attempt);
    }
  }

  throw lastError ?? new Error('Request failed');
};

const request = async <T>(path: string, opts: RequestOptions = {}): Promise<T> => {
  const {
    method = 'GET',
    body,
    token: providedToken,
    preferProvidedToken = false,
    headers = {},
    orgId: providedOrgId,
    retries = 1,
    ignoreOrgRemoval = false,
    skipAuthRecovery = false,
  } = opts;
  const resolvedBody = body === undefined ? undefined : normaliseJsonValue(body);
  const baseHeaders: Record<string, string> = {
    ...(resolvedBody !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...headers,
  };

  return await executeRequest<T>({
    path,
    method,
    headers: baseHeaders,
    body: resolvedBody !== undefined ? JSON.stringify(resolvedBody) : undefined,
    token: providedToken,
    preferProvidedToken,
    orgId: providedOrgId,
    retries,
    ignoreOrgRemoval,
    skipAuthRecovery,
    parseResponse: parseJsonOrUndefined,
  });
};

const requestRaw = async <T>(path: string, opts: RawRequestOptions = {}): Promise<T> => {
  const {
    method = 'GET',
    body,
    token: providedToken,
    preferProvidedToken = false,
    headers = {},
    orgId: providedOrgId,
    retries = 1,
    ignoreOrgRemoval = false,
    skipAuthRecovery = false,
  } = opts;

  return await executeRequest<T>({
    path,
    method,
    headers,
    body: body ?? undefined,
    token: providedToken,
    preferProvidedToken,
    orgId: providedOrgId,
    retries,
    ignoreOrgRemoval,
    skipAuthRecovery,
    parseResponse: parseJsonOrText,
  });
};

export const apiClient = {
  get: async <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    await request<T>(path, { ...opts, method: 'GET' }),
  post: async <T>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> => await request<T>(path, { ...opts, method: 'POST', body }),
  patch: async <T>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> => await request<T>(path, { ...opts, method: 'PATCH', body }),
  put: async <T>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<T> => await request<T>(path, { ...opts, method: 'PUT', body }),
  delete: async <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    await request<T>(path, { ...opts, method: 'DELETE' }),
  raw: async <T>(path: string, opts?: RawRequestOptions): Promise<T> =>
    await requestRaw<T>(path, opts),
  request,
};

export type ApiClient = typeof apiClient;
